import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { cloneDeep, filter, sortBy } from 'lodash'
import React, { FC, useCallback, useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { fetchMemberList } from '../../../../states/containers/member/listSlice'
import { RootState } from '../../../../states/containers/store'
import { createTaskPermission, destroyTaskPermission, fetchTaskPermission, updateTaskPermission } from '../../../../states/containers/task/taskSlice'
import LoadingSquare from '../../../components/common/LoadingSquare'
import Tips from '../../../components/common/Tips'
import AdminModal from '../../../components/task/AdminModal'
import ConfirmationModal from '../../../components/task/ConfirmationModal'
import { useAlert } from 'react-alert'
import { compile } from '../../../../utils/search'

interface ModalState {
  id?: number
  name?: string
  projectSetting?: boolean
  costView?: boolean
  memberCostSetting?: boolean
  projectCostSetting?: boolean
  attendanceExport?: boolean
  projectMemberSetting?: boolean
  type: string
}

interface ConfirmationModalState {
  display: boolean
  type: string
  name: string
  callback: VoidFunction
}

interface List {
  id: number
  name: string
  kana: string | null
  profile_image: string | null
  project_setting: boolean
  cost_view: boolean
  member_cost_setting: boolean
  project_cost_setting: boolean
  attendance_export: boolean
  project_member_setting: boolean
}

const DISPLAY_BLOCK_NUMBER = 10

const Index: FC = () => {
  const {
    login,
    commonPart,
    memberList,
    task,
  } = useSelector((state: RootState) => state)

  const location = useLocation()
  const dispatch = useDispatch()
  const alert = useAlert()

  const [searchWord, setSearchWord] = useState<string>("")
  const [data, setData] = useState<List[]>([])
  const [dataInit, setDataInit] = useState<List[]>([])

  const [isDisplayMember, setIsDisplayMember] = useState<boolean>(false)

  const initModalState = {
    type: "create",
  }

  const [modalState, setModalState] = useState<ModalState>(initModalState)

  const [confirmationModalState, setConfirmationModalState] = useState<ConfirmationModalState>({
    display: false,
    type: "",
    name: "",
    callback: () => {
      // none
    },
  })

  const [sortLabel, setSortLabel] = useState<string>("")
  const [displayBlockNum, setDisplayBlockNum] = useState<number>(1)

  const currentScroll = () => Math.max(window.pageYOffset, document.documentElement.scrollTop, document.body.scrollTop)
  const bottomHeight = () => document.getElementsByClassName("main")[0].clientHeight
  const windowHeight = () => document.documentElement.clientHeight

  const onScroll = useCallback(() => {
    const adjustment = window.innerWidth >= 1280 ? 0.90 : 0.85
    if (
      (bottomHeight() * adjustment) < (currentScroll() + windowHeight())
      && data.length >= displayBlockNum * DISPLAY_BLOCK_NUMBER
    ) {
      setDisplayBlockNum((self) => self + 1)
    }
  }, [displayBlockNum, data])

  const sortDate = (dataList: List[]) => {
    const [key, order] = sortLabel.split("_")
    if (!key || !order) {
      setData(sortBy(dataList || [], 'id'))
      return
    }
    if (order === "asc") {
      setData(sortBy(dataList || [], key))
    } else if (order === "desc") {
      setData(sortBy(dataList || [], key).reverse())
    }
  }

  const search = (words: string) => {
    setSearchWord(words)
    if (!words) {
      setData(cloneDeep(dataInit))
      return
    }
    const word = words.toLowerCase().split(/\s+/)
    const list = filter(dataInit, (value) => {
      if (value === null) return false
      return (compile(word).test(value.name.toLowerCase() + value.kana))
    })
    sortDate(list)
    setDisplayBlockNum(1)
  }

  const reset = () => {
    setSearchWord("")
    sortDate(dataInit)
    setDisplayBlockNum(1)
  }

  const addAdmin = (
    memberId: number,
    projectSetting: boolean,
    costView: boolean,
    memberCostSetting: boolean,
    projectCostSetting: boolean,
    attendanceExport: boolean,
    projectMemberSetting: boolean,
  ) => {
    setIsDisplayMember(false)
    dispatch(
      createTaskPermission(
        {
          member_id: memberId,
          project_setting: projectSetting,
          cost_view: costView,
          member_cost_setting: memberCostSetting,
          project_cost_setting: projectCostSetting,
          attendance_export: attendanceExport,
          project_member_setting: projectMemberSetting,
        },
        () => {
          alert.success("権限を追加しました")
        },
      ),
    )
  }

  const modAdmin = (
    memberId: number,
    projectSetting: boolean,
    costView: boolean,
    memberCostSetting: boolean,
    projectCostSetting: boolean,
    attendanceExport: boolean,
    projectMemberSetting: boolean,
  ) => {
    setIsDisplayMember(false)
    const param = {
      id: memberId,
      project_setting: projectSetting,
      cost_view: costView,
      member_cost_setting: memberCostSetting,
      project_cost_setting: projectCostSetting,
      attendance_export: attendanceExport,
      project_member_setting: projectMemberSetting,
    }
    dispatch(
      updateTaskPermission(
        param,
        () => {
          alert.success("権限を編集しました")
          setData((cur) => cur.map((v) => (v.id === memberId ? { ...v, ...param } : v)))
          setDataInit((cur) => cur.map((v) => (v.id === memberId ? { ...v, ...param } : v)))
        },
      ),
    )
  }

  const openMemberModal = (type: string, id?: number) => {
    if (type === "create") {
      setModalState(
        {
          type: "create",
        },
      )
    } else {
      const member = task.taskPermission?.members.find((v) => v.id === id)
      if (!member) return
      setModalState(
        {
          type: "update",
          id: member.id,
          name: member.name,
          projectSetting: member.project_setting,
          costView: member.cost_view,
          memberCostSetting: member.member_cost_setting,
          projectCostSetting: member.project_cost_setting,
          attendanceExport: member.attendance_export,
          projectMemberSetting: member.project_member_setting,
        },
      )
    }
    setIsDisplayMember(true)
  }

  // confirmation modal
  const openConfirmationModal = (type: string, id: number) => {
    let callback = () => {
      // none
    }
    switch (type) {
      case "power_delete":
        callback = () => { dispatch(destroyTaskPermission({ id: id }, () => { alert.success("権限を削除しました") })) }
        break
      default:
        break
    }
    const name = memberList.members.find((v) => v.id === id)?.name
    setConfirmationModalState({
      ...confirmationModalState,
      display: true,
      type: type,
      name: name || "",
      callback: callback,
    })
  }

  const closeConfirmationModal = () => {
    setConfirmationModalState({
      ...confirmationModalState,
      display: false,
      type: "",
      name: "",
      callback: () => {
        // none
      },
    })
  }

  const buildSortArea = (type: string) => {
    if (sortLabel === `${type}_asc`) {
      return (
        <>
          <FontAwesomeIcon
            icon={['fas', 'sort-alpha-down']}
            style={{ cursor: "pointer" }}
            onClick={() => setSortLabel(`${type}_desc`)}
          />
        </>
      )
    }
    if (sortLabel === `${type}_desc`) {
      return (
        <>
          <FontAwesomeIcon
            icon={['fas', 'sort-alpha-down-alt']}
            style={{ cursor: "pointer" }}
            onClick={() => setSortLabel("")}
          />
        </>
      )
    }
    return (
      <>
        <FontAwesomeIcon
          icon={['fas', 'sort']}
          style={{ cursor: "pointer" }}
          onClick={() => setSortLabel(`${type}_asc`)}
        />
      </>
    )
  }

  const buildMatrix = (status: string) => {
    let ele = <></>
    if (status === "project_setting") {
      ele = (
        <>
          <FontAwesomeIcon icon={['fas', 'cogs']} />
          プロジェクト・カテゴリー設定
        </>
      )
    } else if (status === "cost_view") {
      ele = (
        <>
          <FontAwesomeIcon icon={['fas', 'eye']} />
          コスト表示
        </>
      )
    } else if (status === "member_cost_setting") {
      ele = (
        <>
          <FontAwesomeIcon icon={['fas', 'coins']} />
          メンバーコスト設定
        </>
      )
    } else if (status === "project_cost_setting") {
      ele = (
        <>
          <FontAwesomeIcon icon={['fas', 'coins']} />
          プロジェクトコスト設定
        </>
      )
    } else if (status === "attendance_export") {
      ele = (
        <>
          <FontAwesomeIcon icon={['fas', 'file-download']} />
          勤怠データエクスポート
        </>
      )
    } else if (status === "project_member_setting") {
      ele = (
        <>
          <FontAwesomeIcon icon={['fas', 'users']} />
          プロジェクト参加メンバー設定
        </>
      )
    }
    return (
      <tr>
        <td>
          <div className="label_wrapper">
            <div className={`label ${status}`}>
              {ele}
            </div>
          </div>
        </td>
        <td>
          {
            status === "project_setting" ? (
              <FontAwesomeIcon icon={['fas', 'check']} className="icon green" />
            ) : (
              <FontAwesomeIcon icon={['fas', 'times']} className="icon red" />
            )
          }
        </td>
        <td>
          {
            status === "cost_view" ? (
              <FontAwesomeIcon icon={['fas', 'check']} className="icon green" />
            ) : (
              <FontAwesomeIcon icon={['fas', 'times']} className="icon red" />
            )
          }
        </td>
        <td>
          {
            status === "member_cost_setting" ? (
              <FontAwesomeIcon icon={['fas', 'check']} className="icon green" />
            ) : (
              <FontAwesomeIcon icon={['fas', 'times']} className="icon red" />
            )
          }
        </td>
        <td>
          {
            status === "project_cost_setting" ? (
              <FontAwesomeIcon icon={['fas', 'check']} className="icon green" />
            ) : (
              <FontAwesomeIcon icon={['fas', 'times']} className="icon red" />
            )
          }
        </td>
        {
          login.function_enabled_state?.task_management_approval && (
            <td>
              {
                status === "attendance_export" ? (
                  <FontAwesomeIcon icon={['fas', 'check']} className="icon green" />
                ) : (
                  <FontAwesomeIcon icon={['fas', 'times']} className="icon red" />
                )
              }
            </td>
          )
        }
        {
          login.enable_project_member_setting && (
            <td>
              {
                (status === "project_setting" || status === "project_member_setting") ? (
                  <FontAwesomeIcon icon={['fas', 'check']} className="icon green" />
                ) : (
                  <FontAwesomeIcon icon={['fas', 'times']} className="icon red" />
                )
              }
            </td>
          )
        }
      </tr>
    )
  }

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [location])

  useEffect(() => {
    if (!login.accessToken) return
    if (!commonPart.company?.id) return
    dispatch(
      fetchMemberList(),
    )
  }, [login.accessToken, commonPart.company])

  useEffect(() => {
    setDataInit(sortBy(task.taskPermission?.members, 'id'))
    setData(sortBy(task.taskPermission?.members, 'id'))
  }, [task])

  useEffect(() => {
    dispatch(fetchTaskPermission())
  }, [])

  useEffect(() => {
    if (!searchWord) {
      sortDate(data)
    }
  }, [sortLabel])

  useEffect(() => {
    document.addEventListener("scroll", onScroll)
    return () => {
      document.removeEventListener("scroll", onScroll)
    }
  }, [onScroll])

  return (
    <section className="task_page">
      <Helmet>
        <title>Qasee - 工数管理 - 設定 - 工数管理詳細権限設定</title>
      </Helmet>
      <section className="breadcrumb">
        工数管理
        &nbsp;&gt;&nbsp;
        設定
        &nbsp;&gt;&nbsp;
        工数管理詳細権限設定
        &nbsp;
      </section>
      <section className="page_header">
        <h1>
          工数管理詳細権限設定
        </h1>
        <div className="tools">
          <div className="search_area">
            <input
              type="text"
              placeholder="メンバーを検索"
              onChange={(event) => search(event.target.value)}
              value={searchWord}
            />
            {
              searchWord && (
                <button type="button" className="reset" onClick={reset}>
                  <FontAwesomeIcon icon={['far', 'times-circle']} />
                </button>
              )
            }
          </div>
          <div className="button_area">
            <button
              type="button"
              className="button"
              onClick={() => openMemberModal("create")}
            >
              <FontAwesomeIcon icon={['fas', 'plus']} />
              &nbsp;
              メンバーを追加する
            </button>
          </div>
        </div>
      </section>
      <section className="page_description">
        <p>
          メンバーに工数管理詳細権限を付与することができます。
        </p>
        <p>
          設定すると権限に応じて、表示や設定が可能になります。
        </p>
        <table className="permission_example">
          <thead>
            <tr>
              <th>
                権限 / できること
              </th>
              <th>
                <div
                  style={{
                    display: "flex",
                    flexFlow: "column",
                    gap: "4px",
                  }}
                >
                  <div>
                    <div>
                      プロジェクトの
                    </div>
                    <div>
                      作成・編集
                    </div>
                  </div>
                  <div>
                    <div>
                      カテゴリーの
                    </div>
                    <div>
                      作成・編集
                    </div>
                  </div>
                </div>
              </th>
              <th>
                <div>
                  <div>
                    プロジェクトの
                  </div>
                  <div>
                    コスト表示
                  </div>
                </div>
              </th>
              <th>
                <div>
                  <div>
                    メンバーの
                  </div>
                  <div>
                    コスト設定
                  </div>
                </div>
              </th>
              <th>
                <div>
                  <div>
                    プロジェクトの
                  </div>
                  <div>
                    コスト設定
                  </div>
                </div>
              </th>
              {
                login.function_enabled_state?.task_management_approval && (
                  <>
                    <th>
                      <div>
                        <div>
                          勤怠の
                        </div>
                        <div>
                          データエクスポート
                        </div>
                      </div>
                    </th>
                  </>
                )
              }
              {
                login.enable_project_member_setting && (
                  <>
                    <th>
                      <div>
                        <div>
                          プロジェクトの
                        </div>
                        <div>
                          参加メンバー設定
                        </div>
                      </div>
                    </th>
                  </>
                )
              }
            </tr>
          </thead>
          <tbody>
            {buildMatrix("project_setting")}
            {buildMatrix("cost_view")}
            {buildMatrix("member_cost_setting")}
            {buildMatrix("project_cost_setting")}
            {login.function_enabled_state?.task_management_approval && buildMatrix("attendance_export")}
            {login.enable_project_member_setting && buildMatrix("project_member_setting")}
          </tbody>
        </table>
      </section>
      <section className="page_contents content-area">
        <div className="content-block">
          <div className="block-detail">
            {
              task.isLoading ? (
                <LoadingSquare>
                  <>
                    工数管理詳細権限設定を読み込んでいます…
                  </>
                </LoadingSquare>
              ) : (
                <>
                  <div className="permission_table">
                    <div className="row_head">
                      <div>
                        編集
                      </div>
                      <div>
                        メンバー
                        {buildSortArea("kana")}
                      </div>
                      <div>
                        権限
                      </div>
                    </div>
                    {
                      data.slice(0, displayBlockNum * DISPLAY_BLOCK_NUMBER).map((member) => (
                        <div
                          className="row"
                          key={`row_${member.id}`}
                        >
                          <div>
                            <Tips
                              description="権限を変更する"
                              className=""
                              icon={false}
                            >
                              <>
                                <FontAwesomeIcon
                                  icon={['fas', 'tools']}
                                  style={{ cursor: "pointer" }}
                                  onClick={() => openMemberModal("update", member.id)}
                                />
                              </>
                            </Tips>
                            <Tips
                              description="権限を削除する"
                              className=""
                              icon={false}
                            >
                              <>
                                <FontAwesomeIcon
                                  icon={['fas', 'minus-circle']}
                                  style={{ cursor: "pointer" }}
                                  onClick={() => openConfirmationModal("power_delete", member.id)}
                                />
                              </>
                            </Tips>
                          </div>
                          <div>
                            {
                              member.profile_image ? (
                                <img alt="profile" src={member.profile_image} className="profile_icon" />
                              ) : (
                                <img
                                  alt=""
                                  src={`/static/images/profile/${(Number(member.id) || 0) % 12}.jpeg`}
                                  className="profile_icon"
                                />
                              )
                            }
                            <ruby>
                              {member.name}
                              <rt>{member.kana}</rt>
                            </ruby>
                          </div>
                          <div>
                            {
                              member.project_setting && (
                                <Tips
                                  description="プロジェクト・カテゴリーを設定・編集することができます"
                                  className=""
                                  icon={false}
                                  style={{
                                    height: "auto",
                                  }}
                                >
                                  <div className="label project_setting">
                                    <FontAwesomeIcon icon={['fas', 'cogs']} />
                                    プロジェクト・カテゴリー設定
                                  </div>
                                </Tips>
                              )
                            }
                            {
                              member.cost_view && (
                                <Tips
                                  description="プロジェクトにかかったコストを表示することができます"
                                  className=""
                                  icon={false}
                                  style={{
                                    height: "auto",
                                  }}
                                >
                                  <div className="label cost_view">
                                    <FontAwesomeIcon icon={['fas', 'eye']} />
                                    コスト表示
                                  </div>
                                </Tips>
                              )
                            }
                            {
                              member.member_cost_setting && (
                                <Tips
                                  description="メンバーのコストを設定することができます"
                                  className=""
                                  icon={false}
                                  style={{
                                    height: "auto",
                                  }}
                                >
                                  <div className="label member_cost_setting">
                                    <FontAwesomeIcon icon={['fas', 'coins']} />
                                    メンバーコスト設定
                                  </div>
                                </Tips>
                              )
                            }
                            {
                              member.project_cost_setting && (
                                <Tips
                                  description="プロジェクトのコストを設定することができます"
                                  className=""
                                  icon={false}
                                  style={{
                                    height: "auto",
                                  }}
                                >
                                  <div className="label project_cost_setting">
                                    <FontAwesomeIcon icon={['fas', 'coins']} />
                                    プロジェクトコスト設定
                                  </div>
                                </Tips>
                              )
                            }
                            {
                              login.function_enabled_state?.task_management_approval && member.attendance_export && (
                                <Tips
                                  description="勤怠データをCSV出力することができます"
                                  className=""
                                  icon={false}
                                  style={{
                                    height: "auto",
                                  }}
                                >
                                  <div className="label attendance_export">
                                    <FontAwesomeIcon icon={['fas', 'file-download']} />
                                    勤怠データエクスポート
                                  </div>
                                </Tips>
                              )
                            }
                            {
                              member.project_member_setting && (
                                <Tips
                                  description="プロジェクトの参加メンバーを設定することができます"
                                  className=""
                                  icon={false}
                                  style={{
                                    height: "auto",
                                  }}
                                >
                                  <div className="label project_member_setting">
                                    <FontAwesomeIcon icon={['fas', 'users']} />
                                    プロジェクト参加メンバー設定
                                  </div>
                                </Tips>
                              )
                            }
                          </div>
                        </div>
                      ))
                    }
                    <div className="row_foot">
                      <div>
                        <Tips
                          description="メンバーを追加する"
                          className=""
                          icon={false}
                        >
                          <>
                            <FontAwesomeIcon
                              icon={['fas', 'plus-circle']}
                              style={{ cursor: "pointer" }}
                              onClick={() => openMemberModal("create")}
                            />
                          </>
                        </Tips>
                      </div>
                      <div>
                        <button
                          type="button"
                          onClick={() => openMemberModal("create")}
                        >
                          <FontAwesomeIcon icon={['fas', 'plus']} />
                          &nbsp;
                          メンバーを追加する
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              )
            }
          </div>
        </div>
      </section>
      <AdminModal
        modalState={modalState}
        closeCallback={() => setIsDisplayMember(false)}
        addCallback={
          (memberId, projectSetting, costView, memberCostSetting, projectCostSetting, attendanceExport, projectMemberSetting) => {
            addAdmin(memberId, projectSetting, costView, memberCostSetting, projectCostSetting, attendanceExport, projectMemberSetting)
          }
        }
        modCallback={
          (memberId, projectSetting, costView, memberCostSetting, projectCostSetting, attendanceExport, projectMemberSetting) => {
            modAdmin(memberId, projectSetting, costView, memberCostSetting, projectCostSetting, attendanceExport, projectMemberSetting)
          }
        }
        isDisplay={isDisplayMember}
        memberList={
          memberList.members.filter((v) => !task.taskPermission?.members.map((w) => w.id).includes(v.id)).map((v) => ({ id: v.id, name: v.name }))
        }
      />
      {/* 確認モーダル */}
      <ConfirmationModal
        confirmationModalState={confirmationModalState}
        closeCallback={closeConfirmationModal}
      />
    </section>
  )
}

export default Index
